import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlexaVisibilityQuestion,
  GetAlexaVisibilityQuestionsResponse
} from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import {
  AlexaVisibilityLambdaFetch,
  awaitJSON,
  getUserFacingMessage
} from '@src/utils/fetch-utils';
import { useSetError } from '@src/redux/modals';

// With a brand, returns that brand's own list, or the company-wide list it inherits when the brand
// has none saved. Without one, returns the company-wide list.
export const useAlexaVisibilityQuestions = (
  company: string,
  brand: string
): ReturnType<typeof useQuery<GetAlexaVisibilityQuestionsResponse>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['alexaVisibilityQuestions', company, brand],
    queryFn: async (): Promise<GetAlexaVisibilityQuestionsResponse> => {
      const response = await AlexaVisibilityLambdaFetch('/questions', {
        method: 'GET',
        params: brand ? { company, brand } : { company }
      });
      return awaitJSON<GetAlexaVisibilityQuestionsResponse>(response);
    },
    enabled: Boolean(company)
  });

interface SaveQuestionsVariables {
  company: string;
  // Omit to save the company-wide list.
  brand?: string;
  questions: AlexaVisibilityQuestion[];
}

// Replaces the brand's question list, or the company-wide one when no brand is given.
export const useSaveAlexaVisibilityQuestions = () => {
  const queryClient = useQueryClient();
  const setError = useSetError();

  return useMutation({
    mutationFn: async ({ company, brand, questions }: SaveQuestionsVariables): Promise<void> => {
      await AlexaVisibilityLambdaFetch('/questions', {
        method: 'PUT',
        body: { company, brand, questions }
      });
    },
    // Every brand under the company is invalidated: a company-wide save changes what brands
    // without their own list inherit.
    onSuccess: (_data, { company }) =>
      queryClient.invalidateQueries({ queryKey: ['alexaVisibilityQuestions', company] }),
    onError: e => {
      setError({
        title: "Couldn't save questions",
        message: getUserFacingMessage(e, "Couldn't save questions"),
        reportError: e
      });
    }
  });
};
